import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import EnterpriseService from "../../common/services/enterprise-service";
import reactSelectStylesCreatable from "../../common/plugins/reactSelectStylesCreatable";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import { EnterpriseContext } from "../enterprise/EnterpriseContext";
import truckyService from "../../common/services/trucky-service";
import { confirmWrapper } from "../ui/askConfirm";
import CustomBadge from "../ui/CustomBadge";
import CreatableSelect from "react-select/creatable";
import ProvisioningStatusBadge from "./ProvisioningStatusBadge";
import ServerStatusBadge from "./ServerStatusBadge";
import AsyncSelect from "react-select/async";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import ReactSelectUserOption from "../ui/ReactSelectUserOption";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import {
  HOSTING_2GB_PLAN,
  LEGACY_HOSTING_PLAN,
  SERVER_GENERATION_NAMES,
} from "../../common/constants/hosting";

const provisioningMessages = {
  upgrading: [
    "Creating the new server instance and preparing the hardware...",
    "Moving your server to its new home...",
    "Preparing the upgraded virtual machine...",
  ],
  creating: [
    "Creating your virtual machine...",
    "Allocating CPU, memory and storage...",
    "Connecting the virtual cables...",
    "Booting up the new machine...",
    "Preparing the operating system...",
  ],
  provisioning: [
    "Applying security updates...",
    "Installing the latest system patches...",
    "Installing the game dedicated server...",
    "Configuring the game server...",
    "Checking services and network connectivity...",
    "Almost ready: performing final checks...",
  ],
};

const getRandomProvisioningMessage = (status, currentMessage = "") => {
  const messages = provisioningMessages[status] || ["Preparing your server..."];
  const availableMessages = messages.filter(
    (message) => message != currentMessage,
  );

  return availableMessages[Math.floor(Math.random() * availableMessages.length)];
};

const DedicatedServerConfiguration = ({ container }) => {
  const { globalContext } = useContext(EnterpriseContext);

  const [server, setServer] = useState({
    game_id: 1,
    max_players: 8,
    max_vehicles_total: 100,
    max_ai_vehicles_player: 50,
    max_ai_vehicles_player_spawn: 50,
    player_damage: true,
    traffic: true,
    hide_colliding: true,
    name_tags: true,
    show_server: true,
    moderator_list: [
      container.dataService.enterpriseService.member.steam.external_user_id,
    ],
    mods_optioning: false,
    timezones: "0",
    user_id: globalContext.member.id,
  });

  const navigate = useNavigate();
  let { id } = useParams();
  const es = new EnterpriseService();
  const [loading, setLoading] = useState(false);
  const [provisioningStatus, setProvisioningStatus] = useState(null);
  const [provisioningMessage, setProvisioningMessage] = useState("");
  const [serverPackagesDat, setServerPackagesDat] = useState(null);
  const [serverPackagesSii, setServerPackagesSii] = useState(null);
  const [regions, setRegions] = useState([]);
  const [moderatorsListTextValue, setModeratorsListTexValue] = useState("");
  const [users, setUsers] = useState([]);
  const [usernameToSearch, setUsernameToSearch] = useState("");
  const [betas, setBetas] = useState([]);
  const [userSearchResult, setUserSearchResult] = useState([]);
  const [bans, setBans] = useState([]);
  const [loadingBans, setLoadingBans] = useState(false);
  const [serverNotFound, setServerNotFound] = useState(false);
  const [requiresHostingUpgrade, setRequiresHostingUpgrade] = useState(false);
  const [hasActiveHosting2GbSubscription, setHasActiveHosting2GbSubscription] =
    useState(false);

  useEffect(() => {
    truckyService.setDocumentTitle("Dedicated Server");

    getRegions();
    checkHostingSubscriptionUpgrade();

    if (id) {
      getServer(id);
    }
  }, []);

  useEffect(() => {
    if (server?.game_id) {
      getGameBetas();
    }
  }, [server?.game_id]);

  useEffect(() => {
    if (!provisioningMessages[provisioningStatus]) return;

    const messageInterval = setInterval(() => {
      setProvisioningMessage((currentMessage) =>
        getRandomProvisioningMessage(provisioningStatus, currentMessage),
      );
    }, 6000);

    return () => clearInterval(messageInterval);
  }, [provisioningStatus]);

  const getRegions = async () => {
    const result = await es.getRegions();
    setRegions(result);
  };

  const checkHostingSubscriptionUpgrade = async () => {
    const userSubscriptions = await es.getUserSubscriptions();
    const hasLegacyHostingSubscription = userSubscriptions.subscriptions.some(
      (subscription) =>
        subscription.plan == LEGACY_HOSTING_PLAN &&
        subscription.state != "deactivated",
    );
    const hasActiveHosting2GbSubscription =
      userSubscriptions.subscriptions.some(
        (subscription) =>
          subscription.plan == HOSTING_2GB_PLAN &&
          subscription.state == "active",
      );

    setHasActiveHosting2GbSubscription(hasActiveHosting2GbSubscription);
    setRequiresHostingUpgrade(
      hasLegacyHostingSubscription && !hasActiveHosting2GbSubscription,
    );
  };

  const getServer = async () => {
    setLoading(true);
    const result = await es.getDedicatedServerConfiguration(id);
    if (result && result.id) {
      setServer(result);

      if (
        result.provisioning_status == "creating" ||
        result.provisioning_status == "provisioning"
      ) {
        setProvisioningStatus(result.provisioning_status);
        setProvisioningMessage(
          getRandomProvisioningMessage(result.provisioning_status),
        );
        setTimeout(() => updateProvisioningStatus(result.id), 30000);
      }

      getUsers();
    } else {
      setServerNotFound(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (
      server &&
      server.id &&
      server.provisioning_status == "provisioned" &&
      server.droplet_id &&
      server.droplet_id != null &&
      server.droplet_ip_address != null
    ) {
      getBansList();
    }
  }, [server?.id]);

  const getGameBetas = async () => {
    const result = await es.getGameBetas(server.game_id);
    setBetas(result);
  };

  const save = async () => {
    if (!truckyService.isValidWebhookUrl(server.discord_webhook_url)) {
      toast.error(container.localeManager.strings.discordWebhookUrlNotValid);
      return;
    }

    setLoading(true);

    let result;
    let creating = false;

    if (!server.id) {
      creating = true;

      if (serverPackagesDat != null && serverPackagesSii != null) {
        result = await es.createDedicatedServerConfiguration(server);
      } else {
        toast.error(container.localeManager.strings.pleaseUploadServerPackages);
        setLoading(false);
        return;
      }
    } else {
      result = await es.updateDedicatedServerConfiguration(server);
    }

    if (result.success) {
      id = result.server.id;

      setServer(result.server);

      if (serverPackagesDat != null) {
        const serverPackagesDataUploadResult =
          await es.uploadDedicatedServerConfiguration(
            result.server,
            "server_packages_dat",
            serverPackagesDat,
          );

        if (!serverPackagesDataUploadResult.success) {
          toast.error(serverPackagesDataUploadResult.message);
          setLoading(false);
          return;
        }
      }

      if (serverPackagesSii != null) {
        const serverPackagesSiiUploadResult =
          await es.uploadDedicatedServerConfiguration(
            result.server,
            "server_packages_sii",
            serverPackagesSii,
          );

        if (!serverPackagesSiiUploadResult.success) {
          toast.error(serverPackagesSiiUploadResult.message);
          setLoading(false);
          return;
        }
      }

      if (creating) {
        toast.success("Server Created");
      } else {
        toast.success("Server Updated");
      }
    } else {
      toast.error(result.message);
    }

    setLoading(false);

    if (
      result.server &&
      result.success &&
      result.server.provisioning_status == "not_provisioned"
    ) {
      if (
        await confirmWrapper(
          container.localeManager.strings.readyToBeProvisioned,
          { container },
        )
      ) {
        provisionServer(result.server.id);
      }
    }
  };

  const getSwitch = (key) => {
    const updateCheck = (value) => {
      let newServerData = { ...server };
      newServerData[key] = value;
      setServer(newServerData);
    };

    return (
      <label class="form-check form-switch form-check-custom form-check-solid">
        <input
          disabled={!hasPermission("general")}
          onChange={(e) => updateCheck(e.target.checked)}
          class="form-check-input"
          type="checkbox"
          checked={server[key]}
        />
        <span class="form-check-label">
          {container.localeManager.strings[key]}
        </span>
      </label>
    );
  };

  const deleteServer = async () => {
    if (
      await confirmWrapper(
        container.localeManager.strings.deleteServerConfirm,
        { container },
      )
    ) {
      setLoading(true);
      await es.deleteDedicatedServerConfiguration(server.id);
      toast.success("Server Deleted");
      navigate("/hosting");
      setLoading(false);
    }
  };

  const provisionServer = async (id) => {
    setProvisioningStatus("creating");
    setProvisioningMessage(getRandomProvisioningMessage("creating"));
    const provisionResult = await es.provisionDedicatedServerConfiguration(id);

    if (provisionResult.success) {
      if (provisionResult.server) {
        setServer(provisionResult.server);
        setProvisioningStatus(
          provisionResult.server.provisioning_status || "creating",
        );
        setProvisioningMessage(
          getRandomProvisioningMessage(
            provisionResult.server.provisioning_status || "creating",
          ),
        );
      }
      setTimeout(() => updateProvisioningStatus(id), 30000);
    } else {
      setProvisioningStatus(null);
      toast.error(provisionResult.message);
    }
  };

  const upgradeServer = async () => {
    if (
      !(await confirmWrapper(
        "Upgrading this server creates a new server instance. Provisioning may take longer than usual and the server may be temporarily unavailable. Do you want to continue?",
        { container },
      ))
    ) {
      return;
    }

    setProvisioningStatus("upgrading");
    setProvisioningMessage(getRandomProvisioningMessage("upgrading"));

    const upgradeResult = await es.upgradeDedicatedServerConfiguration(
      server.id,
    );

    if (upgradeResult.success) {
      const upgradedServerId = upgradeResult.server?.id || server.id;

      if (upgradeResult.server) {
        setServer(upgradeResult.server);
        setProvisioningStatus(
          upgradeResult.server.provisioning_status || "provisioning",
        );
        setProvisioningMessage(
          getRandomProvisioningMessage(
            upgradeResult.server.provisioning_status || "provisioning",
          ),
        );
      } else {
        setProvisioningStatus("provisioning");
        setProvisioningMessage(getRandomProvisioningMessage("provisioning"));
      }

      toast.success("Server upgrade started");
      setTimeout(() => updateProvisioningStatus(upgradedServerId), 30000);
    } else {
      setProvisioningStatus(null);
      toast.error(upgradeResult.message || "Failed to upgrade server");
    }
  };

  const updateProvisioningStatus = async (id) => {
    let serverStatus = await es.getDedicatedServerConfiguration(id);
    setServer(serverStatus);
    setProvisioningStatus(serverStatus.provisioning_status);
    setProvisioningMessage((currentMessage) =>
      getRandomProvisioningMessage(
        serverStatus.provisioning_status,
        currentMessage,
      ),
    );
    if (serverStatus.provisioning_status != "provisioned") {
      setTimeout(() => updateProvisioningStatus(id), 30000);
    }
  };

  const getProvisioningStatus = (status) => {
    let color = "danger";

    switch (status) {
      case "not_provisioned":
        color = "danger";
        break;
      case "provisioned":
        color = "success";
        break;
      case "provisioning":
        color = "warning";
        break;
      case "creating":
        color = "warning";
        break;
    }

    return (
      <CustomBadge color={color} size="lg">
        {container.localeManager.strings[status]}
      </CustomBadge>
    );
  };

  const handleKeyDown = (event) => {
    if (!moderatorsListTextValue) return;
    if (moderatorsListTextValue.trim() == "") return;

    switch (event.key) {
      case "Enter":
      case "Tab":
        const reg = new RegExp("^[0-9]+$");
        if (reg.test(moderatorsListTextValue)) {
          let modsList = server.moderator_list;
          if (!modsList) {
            modsList = [];
          }
          modsList.push(moderatorsListTextValue);

          modsList = modsList.filter((m) => m.trim() != "");

          setModeratorsListTexValue("");
          setServer({ ...server, moderator_list: modsList });
          event.preventDefault();
        } else {
          toast.error(
            "Moderator Steam ID is invalid, only numbers accepted, no whitespaces",
          );
          setModeratorsListTexValue("");
        }
    }
  };

  const addModerator = () => {
    if (moderatorsListTextValue && moderatorsListTextValue.trim() != "") {
      let id = moderatorsListTextValue.trim();

      const reg = new RegExp("^[0-9]+$");
      if (reg.test(id)) {
        let modsList = server.moderator_list;
        if (!modsList) {
          modsList = [];
        }
        modsList.push(id);
        setModeratorsListTexValue("");
        setServer({ ...server, moderator_list: modsList });
      } else {
        toast.error(
          "Moderator Steam ID is invalid, only numbers accepted, no whitespaces",
        );
      }
    } else {
      toast.error("Please insert a valid Steam ID");
    }
  };

  const restartServer = async () => {
    setLoading(true);
    await es.restartServer(server.id);
    setLoading(false);
  };

  const getUsers = async () => {
    const result = await es.getDedicatedServerUsers(id);
    setUsers(result);
  };

  const searchUsers = async (usernameToSearch) => {
    if (usernameToSearch != "" && usernameToSearch.length > 2) {
      const result = await es.searchUsers(usernameToSearch);
      return result;
    }
  };

  const addUser = async (user) => {
    if (user) {
      if (users.find((m) => m.user_id == user.id)) {
        return;
      }

      const addUserResult = await es.addDedicatedServerUser(server.id, user);

      if (addUserResult.success) {
        setUsers([...users, addUserResult.user]);
        toast.success("User added");
      } else {
        toast.error(addUserResult.message);
      }
    }
  };

  const removeUser = async (ds_user_id) => {
    setLoading(true);
    const result = await es.removeDedicatedServerUser(server.id, ds_user_id);
    setUsers(users.filter((m) => m.id != ds_user_id));
    setLoading(false);
  };

  const updateUser = async (ds_user_id) => {
    setLoading(true);
    await es.updateDedicatedServerUser(
      server.id,
      users.find((m) => m.id == ds_user_id),
    );
    toast.success("User permissions saved");
    setLoading(false);
  };

  const togglePermissions = (ds_user_id, permission, checked) => {
    const newUsers = users.map((m) => {
      if (m.id == ds_user_id) {
        if (!m.permissions) m.permissions = [];

        if (checked) {
          m.permissions.push(permission);
        } else {
          m.permissions = m.permissions.filter((m) => m != permission);
        }

        return m;
      } else return m;
    });

    setUsers(newUsers);
  };

  const hasPermission = (permission) => {
    if (server != null && server.user_id == globalContext.member.id)
      return true;

    if (users.length > 0) {
      const user = users.find((m) => m.user_id == globalContext.member.id);

      if (server != null && user != null) {
        if (user.permissions && user.permissions.includes(permission))
          return true;
      }
    }

    return false;
  };

  const getBansList = async () => {
    setLoadingBans(true);
    const result = await es.getDedicatedServerBansList(server.id);
    if (result.success) {
      setBans(result.bans);
    }
    setLoadingBans(false);
  };

  const removeUserBan = async (steam_id) => {
    if (
      await confirmWrapper(
        "Are you sure you want to remove this ban? Next, you must restart the server to apply changes.",
        {
          container,
        },
      )
    ) {
      setLoading(true);
      setLoadingBans(true);
      const result = await es.dedicatedServerRemoveUserBan(server.id, steam_id);
      if (result.success) {
        toast.success("Ban removed");
        getBansList();
      } else {
        toast.error(result.message);
        setLoadingBans(false);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner
            color="primary"
            style={{ width: "4rem", height: "4rem" }}
          ></Spinner>
        }
      >
        <Row className="gx-0">
          <Col>
            <Card>
              <CardBody>
                <Breadcrumbs
                  breadcrumbs={[
                    {
                      to: "/hosting",
                      title: container.localeManager.strings.dedicatedServers,
                    },
                    {
                      title: server.lobby_name
                        ? server.lobby_name
                        : "New Server",
                    },
                  ]}
                ></Breadcrumbs>
                {serverNotFound && (
                  <div className="text-center text-danger">
                    Server not found - it may have been deleted or you may not
                    have permissions to see it.
                  </div>
                )}
                {!serverNotFound && (
                  <>
                    <h5 className="d-inline-block w-100 mb-5">
                      {container.localeManager.strings.serverConfiguration}
                    </h5>
                    {provisioningStatus && (
                      <div
                        className={`notice rounded border border-dashed p-5 mb-5 ${
                          provisioningStatus == "provisioned"
                            ? "bg-light-success border-success"
                            : "bg-light-primary border-primary"
                        }`}
                      >
                        <div
                          className={`d-flex justify-content-between align-items-center gap-3 ${
                            provisioningStatus == "provisioned" ? "" : "mb-3"
                          }`}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <FontAwesomeIcon
                              icon={
                                provisioningStatus == "provisioned"
                                  ? "check-circle"
                                  : "server"
                              }
                              className={`fs-2 ${
                                provisioningStatus == "provisioned"
                                  ? "text-success"
                                  : "text-primary"
                              }`}
                            />
                            <div>
                              <div className="fw-bold fs-5">
                                {provisioningStatus == "provisioned"
                                  ? "Server provisioned successfully"
                                  : "Server provisioning in progress"}
                              </div>
                              <div>
                                {provisioningStatus == "provisioned"
                                  ? "Virtual machine has been provisioned, the game dedicated server is starting up, please check the game server logs."
                                  : provisioningMessage}
                              </div>
                            </div>
                          </div>
                          <span
                            className={`badge text-uppercase ${
                              provisioningStatus == "provisioned"
                                ? "badge-success"
                                : "badge-primary"
                            }`}
                          >
                            {provisioningStatus != "provisioned" && (
                              <FontAwesomeIcon
                                icon="spinner"
                                spin
                                className="me-2"
                              />
                            )}
                            {provisioningStatus}
                          </span>
                        </div>
                        {provisioningStatus != "provisioned" && (
                          <>
                            <div
                              className="progress h-10px"
                              role="progressbar"
                              aria-label="Server provisioning progress"
                              aria-valuetext={provisioningStatus}
                            >
                              <div
                                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                style={{ width: "100%" }}
                              ></div>
                            </div>
                            <div className="fs-7 mt-2">
                              This page updates automatically. Provisioning a
                              new server may take longer than usual.
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {requiresHostingUpgrade && (
                      <div className="notice bg-light-warning rounded border-warning border border-dashed p-6 mb-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div>
                          <div className="fw-bold mb-1">
                            <strong>Attention:</strong> Subscription Upgrade
                            Required
                          </div>
                          <div>
                            ETS2 and ATS Update 1.61 requires additional
                            hardware resources. Upgrade your Gen 1 Server
                            subscription to Gen 2 Server to continue using the
                            service.
                            <br />
                            Otherwise you can continue using Gen 1 Server,
                            but the server won't work for 1.61. You can continue
                            to use it for previous versions of the game.
                          </div>
                        </div>
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => navigate("/my/subscriptions")}
                        >
                          <FontAwesomeIcon icon="arrow-up" />
                          {container.localeManager.strings.upgrade || "Upgrade"}
                        </Button>
                      </div>
                    )}
                    {hasActiveHosting2GbSubscription &&
                      !provisioningStatus &&
                      server.user_id == globalContext.member.id &&
                      server.size_id == "s-1vcpu-1gb" &&
                      server.provisioning_status == "provisioned" && (
                        <div className="notice bg-light-success rounded border-success border border-dashed p-6 mb-5 d-flex flex-wrap justify-content-between align-items-center gap-4">
                          <div className="d-flex align-items-start gap-3">
                            <FontAwesomeIcon
                              icon="server"
                              className="fs-2 text-success mt-1"
                            />
                            <div>
                              <div className="fs-4 fw-bold mb-1">
                                Server hardware upgrade available
                              </div>
                              <div>
                                Your subscription includes a hardware upgrade
                                for this server. The upgrade creates a new
                                server instance, so provisioning may take longer
                                than usual and the server may be temporarily
                                unavailable.
                              </div>
                            </div>
                          </div>
                          <Button color="success" onClick={upgradeServer}>
                            <FontAwesomeIcon icon="arrow-up" />
                            Upgrade Server
                          </Button>
                        </div>
                      )}
                    {server.id && (
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <div className="d-flex flex-wrap gap-2">
                          {SERVER_GENERATION_NAMES[server.size_id] && (
                            <CustomBadge
                              color="primary"
                              size="lg"
                            >
                              <FontAwesomeIcon
                                icon="microchip"
                                className="me-1"
                              />
                              {SERVER_GENERATION_NAMES[server.size_id]}
                            </CustomBadge>
                          )}
                          <ProvisioningStatusBadge
                            container={container}
                            status={server.provisioning_status}
                            size="lg"
                          ></ProvisioningStatusBadge>
                          {server.provisioning_status == "provisioned" && (
                            <ServerStatusBadge
                              container={container}
                              server={server}
                              size="lg"
                              className="ms-3"
                            ></ServerStatusBadge>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {!provisioningStatus &&
                            hasPermission("restart") &&
                            server.server_packages_dat != null &&
                            server.server_packages_sii != null &&
                            server.droplet_id == null &&
                            server.provisioning_status == "not_provisioned" && (
                              <Button
                                size="sm"
                                color="success"
                                onClick={() => provisionServer(server.id)}
                                className="me-3"
                              >
                                <FontAwesomeIcon icon="rocket-launch"></FontAwesomeIcon>
                                {
                                  container.localeManager.strings
                                    .provisionServer
                                }
                              </Button>
                            )}
                          {server.user_id == globalContext.member.id && (
                            <Button
                              size="sm"
                              color="danger"
                              onClick={deleteServer}
                            >
                              <FontAwesomeIcon icon="trash-alt"></FontAwesomeIcon>
                              {container.localeManager.strings.deleteServer}
                            </Button>
                          )}
                          {hasPermission("restart") &&
                            server.droplet_id != null &&
                            server.provisioning_status == "provisioned" && (
                              <Button
                                size="sm"
                                color="danger"
                                onClick={restartServer}
                              >
                                <FontAwesomeIcon icon="refresh"></FontAwesomeIcon>
                                {container.localeManager.strings.restartServer}
                              </Button>
                            )}
                        </div>
                      </div>
                    )}
                    {server.provisioning_status == "provisioned" && (
                      <Card
                        body
                        className="bg-light my-3 border border-success"
                      >
                        <div className="d-flex flex-wrap align-items-center gap-3">
                          <div className="d-flex align-items-center me-3">
                            <span className="badge badge-success fs-6 px-3 py-2">
                              <FontAwesomeIcon icon="server" className="me-2" />
                              {server.droplet_ip_address}
                            </span>
                          </div>
                          <div className="vr d-none d-md-block me-2" />
                          <Button
                            color="secondary"
                            size="sm"
                            className="border"
                            onClick={() =>
                              truckyService.openUrlInDefaultBrowser(
                                server.packages_dat_url,
                              )
                            }
                          >
                            <FontAwesomeIcon icon="download" className="me-2" />
                            Packages .dat
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            className="border"
                            onClick={() =>
                              truckyService.openUrlInDefaultBrowser(
                                server.packages_sii_url,
                              )
                            }
                          >
                            <FontAwesomeIcon icon="download" className="me-2" />
                            Packages .sii
                          </Button>
                          <Button
                            tag="a"
                            target="_blank"
                            href={server.server_status_url}
                            color="info"
                            size="sm"
                            className="border"
                          >
                            <FontAwesomeIcon
                              icon="circle-info"
                              className="me-2"
                            />
                            Status API
                          </Button>
                          <Button
                            tag="a"
                            href={server.server_logs_viewer_url}
                            target="_blank"
                            color="warning"
                            size="sm"
                            className="border"
                          >
                            <FontAwesomeIcon
                              icon="file-lines"
                              className="me-2"
                            />
                            Server Logs
                          </Button>
                        </div>
                      </Card>
                    )}
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>{container.localeManager.strings.game} *</Label>
                        <Input
                          disabled={server.id != undefined}
                          value={server.game_id}
                          type="select"
                          onChange={(e) =>
                            setServer({ ...server, game_id: e.target.value })
                          }
                        >
                          <option value={1}>Euro Truck Simulator 2</option>
                          <option value={2}>American Truck Simulator</option>
                        </Input>
                      </Col>
                      {globalContext.member.company_id != null && (
                        <Col xs={12} sm={6} className="pt-6">
                          <label class="form-check form-switch form-check-custom form-check-solid">
                            <input
                              disabled={!hasPermission("general")}
                              onChange={(e) =>
                                setServer({
                                  ...server,
                                  company_id: e.target.checked
                                    ? globalContext.member.company_id
                                    : null,
                                })
                              }
                              class="form-check-input"
                              type="checkbox"
                              checked={
                                globalContext.member.company_id ==
                                server.company_id
                              }
                            />
                            <span class="form-check-label">
                              {container.localeManager.strings.companyServer}
                            </span>
                          </label>
                          <FormText color="muted mt-1">
                            {container.localeManager.strings.companyServerHelp}
                          </FormText>
                        </Col>
                      )}
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Region *
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <Row className="mb-3">
                      <Col>
                        {!server.id && (
                          <div className="grid cols-2 cols-md-4 cols-lg-6">
                            {regions.map((m) => {
                              return (
                                <Card
                                  onClick={() =>
                                    server.id == undefined
                                      ? setServer({
                                          ...server,
                                          region_id: m.slug,
                                        })
                                      : false
                                  }
                                  body
                                  className={classNames({
                                    "bg-light": true,
                                    disabled: server.id != undefined,
                                    "text-center": true,
                                    "border border-success":
                                      server != null &&
                                      server.region_id == m.slug,
                                  })}
                                >
                                  {m.name}
                                </Card>
                              );
                            })}
                          </div>
                        )}
                        {server.id && regions && (
                          <div className="d-flex align-items-center gap-3">
                            {regions
                              .filter((m) => m.slug == server.region_id)
                              .map((m) => (
                                <span className="badge badge-success fs-6 px-3 py-2">
                                  <FontAwesomeIcon
                                    icon="location-dot"
                                    className="me-2"
                                  />
                                  {m.name}
                                </span>
                              ))}
                            <span className="text-muted fs-7">
                              <FontAwesomeIcon icon="lock" className="me-1" />
                              Region cannot be changed after provisioning. Other
                              available regions:{" "}
                              {regions
                                .filter((m) => m.slug != server.region_id)
                                .map((m) => m.name)
                                .join(", ")}
                            </span>
                          </div>
                        )}
                      </Col>
                    </Row>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.lobby_name} *
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.lobby_name}
                          type="text"
                          maxLength={63}
                          onChange={(e) =>
                            setServer({ ...server, lobby_name: e.target.value })
                          }
                        ></Input>
                        <FormText color="muted">
                          {container.localeManager.strings.siiMaxChars}
                        </FormText>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.description}
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.description}
                          type="description"
                          maxLength={63}
                          onChange={(e) =>
                            setServer({
                              ...server,
                              description: e.target.value,
                            })
                          }
                        ></Input>
                        <FormText color="muted">
                          {container.localeManager.strings.siiMaxChars}
                        </FormText>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.welcome_message}
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.welcome_message}
                          type="welcome_message"
                          maxLength={127}
                          onChange={(e) =>
                            setServer({
                              ...server,
                              welcome_message: e.target.value,
                            })
                          }
                        ></Input>
                        <FormText color="muted">
                          {container.localeManager.strings.siiMaxChars}
                        </FormText>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.password}
                        </Label>
                        <Input
                          disabled={!hasPermission("password")}
                          value={server.password}
                          type="text"
                          onChange={(e) =>
                            setServer({ ...server, password: e.target.value })
                          }
                        ></Input>
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Limits &amp; Capacity
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.max_players} *
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.max_players}
                          type="number"
                          max={8}
                          min={1}
                          onChange={(e) =>
                            setServer({
                              ...server,
                              max_players: e.target.value,
                            })
                          }
                        ></Input>
                        <FormText color="muted">
                          Max 8 Players &lt;= 1.49 - Max 128 players &gt;= 1.50
                        </FormText>
                        {server.max_players > 8 && (
                          <div className="text-warning">
                            <a
                              target="_blank"
                              href="https://truckyapp.com/kb/cant-find-my-server-with-more-than-8-slots-in-convoy-browser/"
                              className="text-warning"
                            >
                              <FontAwesomeIcon
                                icon="info-circle"
                                className="me-2"
                              ></FontAwesomeIcon>
                              You are using a server with more than 8 slots: to
                              see it in Convoy Browser, follow this guide{" "}
                              <FontAwesomeIcon
                                icon="up-right-from-square"
                                className="ms-2"
                              ></FontAwesomeIcon>
                            </a>
                          </div>
                        )}
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.max_vehicles_total} *
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.max_vehicles_total}
                          type="number"
                          onChange={(e) =>
                            setServer({
                              ...server,
                              max_vehicles_total: e.target.value,
                            })
                          }
                        ></Input>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {
                            container.localeManager.strings
                              .max_ai_vehicles_player
                          }{" "}
                          *
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.max_ai_vehicles_player}
                          type="number"
                          onChange={(e) =>
                            setServer({
                              ...server,
                              max_ai_vehicles_player: e.target.value,
                            })
                          }
                        ></Input>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {
                            container.localeManager.strings
                              .max_ai_vehicles_player_spawn
                          }{" "}
                          *
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.max_ai_vehicles_player}
                          type="number"
                          onChange={(e) =>
                            setServer({
                              ...server,
                              max_ai_vehicles_player_spawn: e.target.value,
                            })
                          }
                        ></Input>
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Access Control
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.server_logon_token}
                        </Label>
                        <Input
                          disabled={!hasPermission("token")}
                          value={server.server_logon_token}
                          type="text"
                          onChange={(e) =>
                            setServer({
                              ...server,
                              server_logon_token: e.target.value,
                            })
                          }
                        ></Input>
                        <FormText>
                          <a
                            className="text-warning"
                            target="_blank"
                            href="https://truckyapp.com/kb/how-to-create-a-server-logon-token/"
                          >
                            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
                            {
                              container.localeManager.strings
                                .serverLogonTokenHelp
                            }
                          </a>
                        </FormText>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.moderator_list}
                        </Label>
                        <InputGroup>
                          <Input
                            disabled={!hasPermission("moderators")}
                            value={moderatorsListTextValue}
                            placeholder="Steam ID"
                            type="text"
                            onChange={(e) =>
                              setModeratorsListTexValue(e.target.value)
                            }
                          ></Input>
                          <Button color="success" onClick={addModerator}>
                            <FontAwesomeIcon
                              icon="plus"
                              className="mx-0"
                            ></FontAwesomeIcon>
                          </Button>
                        </InputGroup>

                        {server.moderator_list &&
                          server.moderator_list.length > 0 && (
                            <>
                              <div className="mt-3">
                                {hasPermission("moderators") &&
                                  server.moderator_list.map((m) => {
                                    return (
                                      <div
                                        className="badge badge-secondary square me-3 mb-1 border-gray-500 border-hover text-normal"
                                        onClick={() =>
                                          setServer({
                                            ...server,
                                            moderator_list:
                                              server.moderator_list.filter(
                                                (k) => k != m,
                                              ),
                                          })
                                        }
                                      >
                                        {m}{" "}
                                        <FontAwesomeIcon
                                          className="ms-1 me-0"
                                          icon="times"
                                        ></FontAwesomeIcon>
                                      </div>
                                    );
                                  })}
                                {!hasPermission("moderators") &&
                                  server.moderator_list.map((m) => {
                                    return (
                                      <div className="badge badge-secondary square me-3 mb-1 border-gray-500 border-hover text-normal">
                                        {m}
                                      </div>
                                    );
                                  })}
                              </div>
                            </>
                          )}
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Gameplay Settings
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={6} sm={3}>
                        {getSwitch("player_damage")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("traffic")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("hide_in_company")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("hide_colliding")}
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs={6} sm={3}>
                        {getSwitch("force_speed_limiter")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("service_no_collision")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("name_tags")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("friends_only")}
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs={6} sm={3}>
                        {getSwitch("show_server")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("mods_optioning")}
                      </Col>
                      <Col xs={6} sm={3}>
                        {getSwitch("in_menu_ghosting")}
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Server Version
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={6} sm={3}>
                        <Label>
                          {container.localeManager.strings.timezones}
                        </Label>
                        <Input
                          disabled={!hasPermission("general")}
                          value={server.timezones}
                          type="number"
                          min="0"
                          max="2"
                          onChange={(e) =>
                            setServer({ ...server, timezones: e.target.value })
                          }
                        ></Input>
                      </Col>
                      <Col xs={12} sm={4}>
                        <Label>Game Server Version</Label>
                        <Input
                          type="select"
                          value={server.beta}
                          onChange={(e) =>
                            setServer({ ...server, beta: e.target.value })
                          }
                        >
                          <option value="">Public</option>
                          {betas.map((m) => {
                            return (
                              <option value={m.name}>{m.description}</option>
                            );
                          })}
                        </Input>
                        <FormText color="muted">
                          Use "Public" for the latest Game Server version
                          available on Steam otherwise a previous version for
                          unsupported mods.
                        </FormText>
                      </Col>
                      <Col xs={12} sm={4}>
                        <Label>Container Server Version</Label>
                        <Input
                          type="select"
                          value={server.docker_image_version}
                          onChange={(e) =>
                            setServer({
                              ...server,
                              docker_image_version: e.target.value,
                            })
                          }
                        >
                          <option value="latest">Latest Public</option>
                          <option value="2026.3">
                            2026.3 (Public Preview ended - please switch to
                            Public)
                          </option>
                        </Input>
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Server Packages
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.server_packages_dat}{" "}
                          *
                        </Label>
                        <input
                          disabled={!hasPermission("server_packages")}
                          accept=".dat"
                          type="file"
                          className="form-control form-control-sm"
                          onChange={(e) =>
                            setServerPackagesDat(e.target.files[0])
                          }
                        ></input>
                        <FormText>
                          <a
                            className="text-warning"
                            target="_blank"
                            href="https://truckyapp.com/kb/how-to-export-server-packages-dat-and-sii/"
                          >
                            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
                            {
                              container.localeManager.strings
                                .exportServerPackagesHelp
                            }
                          </a>
                        </FormText>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Label>
                          {container.localeManager.strings.server_packages_sii}{" "}
                          *
                        </Label>
                        <input
                          disabled={!hasPermission("server_packages")}
                          accept=".sii"
                          type="file"
                          className="form-control form-control-sm"
                          onChange={(e) =>
                            setServerPackagesSii(e.target.files[0])
                          }
                        ></input>
                        <FormText>
                          <a
                            className="text-warning"
                            target="_blank"
                            href="https://truckyapp.com/kb/how-to-export-server-packages-dat-and-sii/"
                          >
                            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
                            {
                              container.localeManager.strings
                                .exportServerPackagesHelp
                            }
                          </a>
                        </FormText>
                      </Col>
                    </FormGroup>
                    <div className="d-flex align-items-center my-4">
                      <h6 className="text-muted text-uppercase fw-bold fs-7 mb-0 me-3">
                        Discord Integration
                      </h6>
                      <hr className="flex-grow-1 mb-0" />
                    </div>
                    <FormGroup row>
                      <Col xs={12} sm={10}>
                        <Label>
                          {container.localeManager.strings.discord_webhook_url}{" "}
                          *
                        </Label>
                        <Input
                          disabled={!hasPermission("webhook")}
                          value={server.discord_webhook_url}
                          type="text"
                          onChange={(e) =>
                            setServer({
                              ...server,
                              discord_webhook_url: e.target.value,
                            })
                          }
                        ></Input>
                        <FormText color="muted">
                          {container.localeManager.strings.discordWebhookHelp}
                        </FormText>
                      </Col>
                      <Col xs={12} sm={2}>
                        <Label>Webhook Mode</Label>
                        <Input
                          type="select"
                          value={server.webhook_mode}
                          onChange={(e) =>
                            setServer({
                              ...server,
                              webhook_mode: e.target.value,
                            })
                          }
                        >
                          <option value="log">
                            Log (One message per join and disconnect)
                          </option>
                          <option value="single-message">
                            Single Message (One message with all current players
                            on join and disconnect)
                          </option>
                        </Input>
                      </Col>
                    </FormGroup>
                    <hr className="mt-5" />
                    <div className="d-flex justify-content-end mt-4">
                      <Button onClick={save} color="primary" size="sm">
                        <FontAwesomeIcon icon="floppy-disk" className="me-2" />
                        {container.localeManager.strings.save}
                      </Button>
                    </div>

                    {bans.length > 0 && !loadingBans && (
                      <Card className="mt-5 mx-0 border border-danger">
                        <CardBody>
                          <h5 className="d-inline-block w-100 mb-3">
                            <FontAwesomeIcon
                              icon="ban"
                              className="me-2 text-danger"
                            />
                            Banned Users
                          </h5>
                          <Table
                            responsive
                            striped
                            size="sm"
                            className="table-row-dashed table-row-gray-300 align-middle gs-0"
                            hover
                          >
                            <thead>
                              <tr className="fw-bolder text-gray-700 bg-light">
                                <th className="text-start ps-2">Steam ID</th>
                                <th className="text-start">
                                  {container.localeManager.strings.reason}
                                </th>
                                <th className="text-start">Expire at</th>
                                <th className="text-start">Note</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {bans.map((m) => {
                                return (
                                  <tr>
                                    <td className="ps-2">{m.steam_id}</td>
                                    <td>{m.username}</td>
                                    <td>
                                      {m.is_permanent && <span>Permanent</span>}
                                      {!m.is_permanent &&
                                        m.ban_end_date != null && (
                                          <span>
                                            {new Date(
                                              m.ban_end_date,
                                            ).toLocaleString()}
                                          </span>
                                        )}
                                    </td>
                                    <td>{m.ban_note}</td>
                                    <td>
                                      <Button
                                        size="sm"
                                        color="danger"
                                        onClick={() =>
                                          removeUserBan(m.steam_id)
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon="trash-alt"
                                          className="me-0"
                                        ></FontAwesomeIcon>
                                        {container.localeManager.strings.unban}
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </CardBody>
                      </Card>
                    )}
                    {server != null &&
                      server.id &&
                      server.user_id == globalContext.member.id && (
                        <Card className="mt-5 mx-0">
                          <CardBody>
                            <h5 className="d-inline-block w-100 mb-3">
                              <FontAwesomeIcon icon="users" className="me-2" />
                              Additional Users
                            </h5>
                            <div className="alert alert-info mb-3 py-2">
                              <FontAwesomeIcon
                                icon="info-circle"
                                className="me-2"
                              ></FontAwesomeIcon>
                              {
                                container.localeManager.strings
                                  .additionalUsersHint
                              }
                            </div>
                            <FormGroup row>
                              <Col sm={4}>
                                <AsyncSelect
                                  isClearable
                                  cacheOptions
                                  getOptionValue={(m) => m.id}
                                  styles={reactSelectCustomStyles}
                                  placeholder="Search by Trucky username"
                                  loadOptions={searchUsers}
                                  components={{ Option: ReactSelectUserOption }}
                                  onChange={(e) => addUser(e)}
                                ></AsyncSelect>
                              </Col>
                            </FormGroup>
                            {users.length > 0 && (
                              <div className="mb-3">
                                <Table
                                  responsive
                                  striped
                                  size="sm"
                                  className="table-row-dashed table-row-gray-300 align-middle gs-0"
                                  hover
                                >
                                  <thead>
                                    <tr className="fw-bolder text-gray-700 bg-light">
                                      <th className="text-start ps-2">
                                        {container.localeManager.strings.user}
                                      </th>
                                      <th className="text-start">
                                        {
                                          container.localeManager.strings
                                            .permissions
                                        }
                                      </th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {users.map((m) => {
                                      return (
                                        <tr>
                                          <td className="ps-2">
                                            <div className="d-flex align-items-center">
                                              <img
                                                src={m.user.avatar_url}
                                                className="avatar h-30px rounded me-3"
                                              />{" "}
                                              {m.user.name}
                                            </div>
                                          </td>
                                          <td>
                                            <Row>
                                              <Col sm={3}>
                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "general",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "general",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    General Properties
                                                  </span>
                                                </label>
                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "restart",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "restart",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Restart Server
                                                  </span>
                                                </label>
                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "webhook",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "webhook",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Change Discord Webhook
                                                  </span>
                                                </label>
                                              </Col>
                                              <Col sm={3}>
                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "server_packages",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "server_packages",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Upload Server Packages
                                                  </span>
                                                </label>

                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "password",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "password",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Change Password
                                                  </span>
                                                </label>
                                              </Col>
                                              <Col sm={3}>
                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "token",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "token",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Change Token
                                                  </span>
                                                </label>

                                                <label class="form-check form-switch form-check-custom form-check-solid me-3 mb-2">
                                                  <input
                                                    checked={m.permissions?.includes(
                                                      "moderators",
                                                    )}
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                      togglePermissions(
                                                        m.id,
                                                        "moderators",
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                  <span class="form-check-label">
                                                    Change Moderators
                                                  </span>
                                                </label>
                                              </Col>
                                            </Row>
                                          </td>
                                          <td className="text-end pe-3">
                                            <Button
                                              className="btn-icon w-30px h-30px ms-auto me-3"
                                              color="active-light-success"
                                              onClick={() => updateUser(m.id)}
                                            >
                                              <FontAwesomeIcon
                                                className="me-0"
                                                icon="check"
                                              ></FontAwesomeIcon>
                                            </Button>
                                            <Button
                                              className="btn-icon w-30px h-30px ms-auto"
                                              color="active-light-danger"
                                              size="small"
                                              onClick={() => removeUser(m.id)}
                                            >
                                              <FontAwesomeIcon
                                                className="me-0"
                                                icon="times"
                                              ></FontAwesomeIcon>
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
          <aside className="ad-column-placeholder">
            {!window.overwolf && (
              <BoxDynamicAd container={container} className="my-3" />
            )}
          </aside>
        </Row>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </LoadingOverlay>
    </>
  );
};

export default DedicatedServerConfiguration;
